import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { App, Image, Upload, type UploadProps } from 'antd'
import { Paperclip, UploadCloud } from 'lucide-react'
import { EMPTY_ATTACHMENT_FILES } from '@/features/files/const/attachment-files.ts'
import { EMPTY_FILE_IDS } from '@/features/files/const/attachment-upload.ts'
import type { AttachmentUploadProps, ManagedUploadFile, UploadedFile } from '@/features/files/types/attachment.ts'
import { getFileId } from '@/features/files/utils/attachment.ts'
import { useTRPC } from '@/trpc/react.ts'
import { openDownload } from '@/utils/download.ts'

export function AttachmentUpload({
  documentKind,
  purpose,
  accept,
  disabled,
  readOnly = false,
  initialFiles = EMPTY_ATTACHMENT_FILES,
  listType = 'picture-card',
  maxCount,
  multiple = true,
  onChange,
  onUploaded,
  value = EMPTY_FILE_IDS
}: AttachmentUploadProps) {
  const trpc = useTRPC()
  const { message } = App.useApp()
  const uploadFile = useMutation(trpc.file.upload.mutationOptions())
  const uploadCompanyDocument = useMutation(trpc.file.uploadCompanyDocument.mutationOptions())
  const uploadIdentityDocument = useMutation(trpc.file.uploadIdentityDocument.mutationOptions())
  const deleteCacheFile = useMutation(trpc.file.deleteCache.mutationOptions())
  const temporaryIds = useRef(new Set<number>())
  const [localFileList, setLocalFileList] = useState<ManagedUploadFile[]>([])
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewIndex, setPreviewIndex] = useState(0)
  const urls = useQuery({
    ...trpc.file.getUrls.queryOptions({ ids: value }),
    enabled: value.length > 0
  })
  const urlById = new Map((urls.data ?? []).map((file) => [file.id, file.url] as const))

  useEffect(() => {
    const valueIds = new Set(value)
    setLocalFileList((current) => {
      const next = current.filter((file) => {
        const id = getFileId(file)
        return !id || valueIds.has(id) || file.status !== 'done'
      })
      return next.length === current.length ? current : next
    })
  }, [value])

  const fileList = (() => {
    const descriptors = new Map(initialFiles.map((file) => [file.id, file]))
    const localById = new Map(
      localFileList.flatMap((file) => {
        const id = getFileId(file)
        return id ? [[id, file] as const] : []
      })
    )
    const controlledFiles = value.map((id) => {
      const existing = localById.get(id)
      const descriptor = descriptors.get(id)
      return {
        ...existing,
        fileId: id,
        name: existing?.name ?? descriptor?.name ?? `附件 ${id}`,
        size: existing?.size ?? descriptor?.size,
        status: 'done' as const,
        type: existing?.type ?? descriptor?.fileType ?? undefined,
        uid: existing?.uid ?? `file-${id}`,
        url: urlById.get(id) ?? existing?.url ?? descriptor?.url
      }
    })
    const transientFiles = localFileList.filter((file) => {
      const id = getFileId(file)
      return !id || file.status !== 'done'
    })
    return [...transientFiles, ...controlledFiles]
  })()

  const previewImages = (() => {
    const images: string[] = []
    for (const file of fileList) {
      if (file.type?.startsWith('image/') && file.url) {
        images.push(file.url)
      }
    }
    return images
  })()

  const handleChange: UploadProps<UploadedFile>['onChange'] = (info) => {
    const nextFiles = info.fileList.map<ManagedUploadFile>((file) => ({
      ...file,
      ...(file.response
        ? {
            fileId: file.response.id,
            name: file.response.originName,
            size: file.response.fileSize,
            type: file.response.fileType ?? undefined,
            url: file.response.url
          }
        : {})
    }))
    setLocalFileList(nextFiles)
    onChange?.(
      Array.from(
        new Set(
          nextFiles.flatMap((file) => {
            const id = file.status === 'done' ? getFileId(file) : undefined
            return id ? [id] : []
          })
        )
      )
    )
  }

  const handlePreview = (file: ManagedUploadFile) => {
    if (!file.url) {
      return
    }
    if (!file.type?.startsWith('image/')) {
      openDownload(file.url, file.name)
      return
    }
    const index = previewImages.indexOf(file.url)
    setPreviewIndex(Math.max(index, 0))
    setPreviewOpen(true)
  }

  const handleRemove = async (file: ManagedUploadFile) => {
    const id = getFileId(file)
    if (!id || !temporaryIds.current.has(id)) {
      return true
    }
    try {
      await deleteCacheFile.mutateAsync({ id })
      temporaryIds.current.delete(id)
      return true
    } catch (error) {
      void message.error(error instanceof Error ? error.message : '临时附件删除失败')
      return false
    }
  }

  const customRequest: NonNullable<UploadProps<UploadedFile>['customRequest']> = (options) => {
    if (!(options.file instanceof Blob)) {
      options.onError?.(new Error('无法读取所选文件'))
      return
    }
    const formData = new FormData()
    formData.set('file', options.file)
    if (purpose) formData.set('purpose', purpose)
    const upload =
      documentKind === 'company'
        ? uploadCompanyDocument
        : documentKind === 'identity'
          ? uploadIdentityDocument
          : uploadFile
    void upload
      .mutateAsync(formData)
      .then((result) => {
        if (!documentKind) {
          temporaryIds.current.add(result.id)
        }
        if (options.file instanceof File) {
          onUploaded?.(options.file, result)
        }
        options.onSuccess?.(result)
      })
      .catch((error: unknown) => {
        options.onError?.(error instanceof Error ? error : new Error('附件上传失败'))
      })
  }

  const uploadLimitReached = maxCount !== undefined && fileList.length >= maxCount

  return (
    <>
      <Upload<UploadedFile>
        accept={accept}
        customRequest={customRequest}
        disabled={disabled || readOnly}
        fileList={fileList}
        listType={listType}
        maxCount={maxCount}
        multiple={multiple}
        onChange={handleChange}
        onDownload={(file) => {
          if (file.url) {
            openDownload(file.url, file.name)
          }
        }}
        onPreview={handlePreview}
        onRemove={handleRemove}
        openFileDialogOnClick={!uploadLimitReached}
        showUploadList={{ showDownloadIcon: true, showPreviewIcon: true, showRemoveIcon: !disabled && !readOnly }}
      >
        {uploadLimitReached || readOnly ? null : (
          <button
            aria-label="选择文件"
            className="text-content-muted flex cursor-pointer flex-col items-center gap-1 border-0 bg-transparent p-0 text-xs disabled:cursor-not-allowed"
            disabled={disabled || readOnly}
            type="button"
          >
            <UploadCloud aria-hidden className="size-5" />
            选择文件
          </button>
        )}
      </Upload>
      {!readOnly && (
        <div className="text-content-soft mt-2 flex items-center gap-1 text-xs">
          <Paperclip aria-hidden size={13} />
          {documentKind ? '证件上传后独立保存，取消订单不会删除已归档资料' : '文件上传成功后将在保存时正式归档'}
        </div>
      )}
      <Image.PreviewGroup
        items={previewImages}
        preview={{
          current: previewIndex,
          onChange: setPreviewIndex,
          onOpenChange: setPreviewOpen,
          open: previewOpen
        }}
      />
    </>
  )
}

export type { AttachmentDescriptor } from '@/features/files/types/attachment.ts'
